import clsx from 'clsx';
import { useState } from 'react';
import type { LinkDescriptor } from 'react-router';

import { routes } from '@/assets/constants/routes-no.ts';
import { CompetitionWinnersDialog } from '@/components/CompetitionWinnersDialog/index.ts';
import { LoanExampleText } from '@/components/LoanExampleText/index.ts';
import { UnderlinedText } from '@/components/UnderlinedText/index.ts';
import { sendUserPortalEvent } from '@/lib/analytics/unified.ts';
import type { CompetitionWinner, CompetitionWithConfig } from '@/lib/api/requests.ts';

import heroMobileAvif from '../_assets/hero-image-mobile.avif';
import heroMobileWebp from '../_assets/hero-image-mobile.webp';
import heroAvif from '../_assets/hero-image.avif';
import heroWebp from '../_assets/hero-image.webp';
import winnerImage from '../_assets/winner.webp';
import { CTA } from '../_components/CTA.tsx';
import { useHasEnded } from '../_components/use-has-ended.ts';

/** Enhances regular image placeholder to be used in thumbnails */
const imagePlaceholder = (placeholder: string, width: number, height: number) =>
  'data:image/svg+xml,' +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}"><filter id="a"><feGaussianBlur stdDeviation="${
      Math.min(width, height) / 20
    }"/><feColorMatrix values="1 0 0 0 0 0 1 0 0 0 0 0 1 0 0 0 0 0 0 1"/></filter><image width="100%" height="100%" filter="url(#a)" href="${placeholder}" preserveAspectRatio="xMidYMid slice"/></svg>`,
  );

const lqip =
  'data:image/webp;base64,UklGRkAAAABXRUJQVlA4IDQAAADQAQCdASoQAAgABUB8JagCdAEOJFFuAAD+xTxSgSnnnerUxOyD4Nm4y1Cn3ZbR+iAMUAAA';

const links: LinkDescriptor[] = [
  {
    href: heroAvif,
    rel: 'preload',
    as: 'image',
  },
];

export const TopSection = (props: {
  activeCompetition: CompetitionWithConfig | undefined;
  winners: CompetitionWinner[] | undefined;
  allWinners: CompetitionWinner[];
  displayWarningWhenKIn: string[] | undefined;
}) => {
  return (
    <>
      <div>
        <div className="relative isolate flex min-h-[min(calc(100dvh-var(--top-bar-height)),var(--max-h))] w-full flex-col overflow-hidden text-white [--max-h:850px] sm:[--max-h:1100px] md:[--max-h:1250px] xl:[--max-h:1100px]">
          <div className="mx-auto w-full p-6 lg:px-0">
            <Content
              activeCompetition={props.activeCompetition}
              allWinners={props.allWinners}
              winners={props.winners}
              displayWarningWhenKIn={props.displayWarningWhenKIn}
            />
          </div>

          {/* Background */}
          <div className="-z-10">
            <picture>
              <source srcSet={heroMobileAvif} type="image/avif" media="(max-width: 1280px)" />
              <source srcSet={heroMobileWebp} type="image/webp" media="(max-width: 1280px)" />
              <source srcSet={heroAvif} type="image/avif" />
              <source srcSet={heroWebp} type="image/webp" />
              <img
                src={heroWebp}
                alt=""
                className="absolute inset-0 size-full origin-bottom-right bg-cover bg-center bg-no-repeat object-cover object-bottom-right xl:object-center min-[120rem]:object-bottom"
                fetchPriority="high"
                loading="eager"
                style={{
                  backgroundImage: `url('${imagePlaceholder(lqip, 16, 9)}')`,
                }}
              />
            </picture>
            <div
              className="absolute inset-0"
              style={{
                background:
                  'linear-gradient(180deg, rgba(0, 106, 255, 0.57) 0%, rgba(217, 217, 217, 0.00) 59.13%)',
              }}
            />
          </div>
        </div>

        {/* Disclaimer */}
        <div className="bg-white pt-4">
          <div className="container mx-auto max-w-3xl px-5 text-center typography-v1-body4 text-balance">
            <LoanExampleText />
          </div>
        </div>
      </div>

      <div className="hidden px-4 max-md:block">
        <div className="mx-auto max-w-sm rounded-2xl bg-v1-background-60 p-4 pt-6 pb-8">
          <Winner allWinners={props.allWinners} />
        </div>
      </div>
    </>
  );
};

const Winner = (props: { allWinners: CompetitionWinner[] }) => {
  const [isAllOpen, setIsAllOpen] = useState(false);

  return (
    <section className="flex flex-col items-center gap-4 text-center">
      <h2 className="typography-v1-h4">Mouaad Joumani vant 330&nbsp;331&nbsp;kr!</h2>
      <p className="typography-v1-body4 text-pretty">
        31. mars fikk 52 år gamle Mouaad Joumani fra Trysil slettet hele sin
        forbruks-/kredittkortgjeld på 330&nbsp;331&nbsp;kr!
      </p>

      <img src={winnerImage} alt="Mouaad Joumani" width={120} height={120} />

      <button
        onClick={() => setIsAllOpen(true)}
        className="typography-v1-button2 text-v1-primary-100"
      >
        Se alle vinnerene
      </button>

      <CompetitionWinnersDialog
        open={isAllOpen}
        onClose={() => setIsAllOpen(false)}
        winners={props.allWinners}
      />
    </section>
  );
};

const Card = (props: React.ComponentProps<'div'>) => (
  <div
    {...props}
    className={clsx(
      'rounded-2xl bg-v1-background-20 p-4 text-v1-background-100 sm:p-8',
      props.className,
    )}
    style={{
      boxShadow: '2px 4px 40px 10px rgba(199, 205, 209, 0.20)',
      ...props.style,
    }}
  />
);

const Content = (props: {
  activeCompetition: CompetitionWithConfig | undefined;
  allWinners: CompetitionWinner[];
  winners: CompetitionWinner[] | undefined;
  displayWarningWhenKIn: string[] | undefined;
}) => {
  const ended = useHasEnded();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="flex flex-col pt-4 pb-64 sm:pb-100 md:pt-8 md:pb-120 xl:pb-20">
      <h1 className="mb-6 text-center text-4xl leading-[1.2] font-bold sm:mb-12 sm:text-5xl md:text-6xl lg:text-7xl xl:mb-16 xl:text-8xl">
        Send{' '}
        <UnderlinedText variant="2" distance="0.2em">
          gjelden din
        </UnderlinedText>{' '}
        på ferie
      </h1>

      <div className="mx-auto max-w-4xl">
        <Card className="mb-4 text-center typography-v1-body1">
          Vinn <span className="font-bold text-v1-primary-100">1 års pause</span> fra gjelden din.
          Scoopr overtar regningene på din forbruks- og kredittkortgjeld i ett helt år!
        </Card>

        <div className="flex flex-col gap-4 md:flex-row">
          <Card className="flex-1">
            <h3 className="mb-2 typography-v1-h3 md:mb-4">For å delta:</h3>
            <ul className="mb-4 flex flex-col gap-3">
              <li className="flex items-center gap-3 typography-v1-body3">
                <div className="grid aspect-square size-8 items-center justify-center rounded-full bg-v1-secondary-100 typography-v1-h4 text-v1-neutral-100">
                  1
                </div>
                Logg inn med bankID
              </li>
              <li className="flex items-center gap-3 typography-v1-body3">
                <div className="grid aspect-square size-8 items-center justify-center rounded-full bg-v1-secondary-100 typography-v1-h4 text-v1-neutral-100">
                  2
                </div>
                Søk om lavere rente
              </li>
              <li className="flex items-center gap-3 typography-v1-body3">
                <div className="grid aspect-square size-8 items-center justify-center rounded-full bg-v1-secondary-100 typography-v1-h4 text-v1-neutral-100">
                  3
                </div>
                Delta i trekningen
              </li>
            </ul>

            <a
              href={routes.JUNE_2026_COMPETITION_TERMS}
              className="mb-6 block typography-v1-body4 underline"
            >
              Vilkår og betingelser for deltagelse
            </a>

            {ended && (
              <p className="-mt-4 mb-4 typography-v1-h4">
                <span>Denne trekningen er avsluttet. </span>
                <button
                  onClick={() => setIsOpen(true)}
                  className="mb-0.5 ml-1 font-semibold text-[#18A0FB] underline"
                >
                  Se vinneren her
                </button>
              </p>
            )}

            <CTA
              className="w-full"
              activeCompetition={props.activeCompetition}
              displayWarningWhenKIn={props.displayWarningWhenKIn}
              onClick={() => {
                sendUserPortalEvent('jun26_competition_cta_top_clicked');
              }}
            />
          </Card>

          <Card className="hidden flex-1 flex-col gap-4 md:block">
            <Winner allWinners={props.allWinners} />
          </Card>
        </div>

        {props.winners && (
          <CompetitionWinnersDialog
            open={isOpen}
            onClose={() => setIsOpen(false)}
            winners={props.winners}
          />
        )}
      </div>
    </div>
  );
};

TopSection.links = links;
